import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, X, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Session type tokens — light theme per UI Handoff May-2026
const SESSION_TYPES = {
  intake: { label: "New Intake", color: "#2563EB", bg: "#EFF6FF" },
  initial: { label: "Preliminary Assessment", color: "#7C3AED", bg: "#F5F3FF" },
  detailed: { label: "In-Depth Assessment", color: "#D97706", bg: "#FFFBEB" },
  call: { label: "Call Booked", color: "#059669", bg: "#ECFDF5" },
  start: { label: "Project Start", color: "#DC2626", bg: "#FEF2F2" },
  end: { label: "Project End", color: "#6B7280", bg: "#F9FAFB" },
};

const TYPE_REMAP = {
  new_intake: "intake",
  initial_assessment: "initial",
  detailed_assessment: "detailed",
  call_booked: "call",
  project_start: "start",
  project_end: "end",
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtDate = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const initialsOf = (name) => {
  if (!name || name === "—") return "—";
  return name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
};

export default function CalendarView({ session }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [consultantFilter, setConsultantFilter] = useState("all");
  const [consultants, setConsultants] = useState([]);
  const [expandedCells, setExpandedCells] = useState(new Set());
  const [detailEvent, setDetailEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const headers = {
        "x-tenant-id": session?.tenant?.id || "tenant_1",
        "x-user-id": session?.user?.id || "admin",
        "x-user-role": session?.user?.role || "admin",
      };
      const [pResp, empResp, sResp, tResp, calResp] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crm/prospects`, { headers }),
        fetch(`${BACKEND_URL}/api/employees/lookup/consultants`),
        fetch(`${BACKEND_URL}/api/assessments/sessions`, { headers }),
        fetch(`${BACKEND_URL}/api/tenants/provisioned`, { headers }),
        fetch(`${BACKEND_URL}/api/webhooks/calendly/sessions`, { headers }),
      ]);
      const prospects = pResp.ok ? await pResp.json() : [];
      const emps = empResp.ok ? await empResp.json() : [];
      const sessions = sResp.ok ? await sResp.json() : [];
      const tenants = tResp.ok ? await tResp.json() : [];
      const calendlySessions = calResp.ok ? await calResp.json() : [];
      setConsultants(emps);

      const calEvents = [];
      const getConsultant = (prospect) => {
        const name = prospect?.assigned_consultant_name
          || (prospect?.assigned_consultant ? `${prospect.assigned_consultant.first_name || ""} ${prospect.assigned_consultant.last_name || ""}`.trim() : "")
          || prospect?.free_assessment_consultant_name
          || prospect?.detailed_assessment_consultant_name
          || "";
        // Per UI brief: unassigned shows as "—" not "Unassigned"
        return name && name !== "Unassigned" ? name : null;
      };

      for (const p of prospects) {
        const company = p.account?.name || p.intake?.company || p.company_name;
        if (!company) continue;
        const consultant = getConsultant(p);
        const createdDate = p.created_at;
        if (createdDate) {
          calEvents.push({
            id: `intake_${p.id}`,
            type: "intake",
            date: new Date(createdDate),
            company,
            contact: p.contact?.full_name || "",
            consultant,
            status: p.stage,
            stage: p.stage,
            prospect_id: p.id,
          });
        }
        if (p.interactions) {
          for (const int of p.interactions) {
            if (int.type === "call_booked" || int.type === "call" || (int.summary || "").toLowerCase().includes("call")) {
              calEvents.push({
                id: `call_${int.id || p.id}_${int.created_at}`,
                type: "call",
                date: new Date(int.created_at),
                company,
                contact: p.contact?.full_name || "",
                consultant,
                status: "scheduled",
                notes: int.summary,
                prospect_id: p.id,
              });
            }
          }
        }
      }

      for (const s of sessions) {
        const prospect = prospects.find((p) => p.id === s.prospect_id);
        const company = prospect?.account?.name || prospect?.intake?.company || prospect?.company_name;
        if (!company) continue;
        const consultant = s.consultant_name || (prospect ? getConsultant(prospect) : null);
        const date = s.scheduled_date || s.created_at;
        if (!date) continue;
        const t = s.assessment_type === "initial" ? "initial" : "detailed";
        calEvents.push({
          id: s.id,
          type: t,
          date: new Date(date),
          company,
          contact: prospect?.contact?.full_name || "",
          consultant: consultant && consultant !== "Unassigned" ? consultant : null,
          status: s.status,
          stage: prospect?.stage,
          prospect_id: prospect?.id,
        });
      }

      for (const t of tenants) {
        try {
          const projResp = await fetch(`${BACKEND_URL}/api/tenants/${t.id}/project`, { headers });
          if (!projResp.ok) continue;
          const projData = await projResp.json();
          const project = projData?.project;
          if (!project) continue;
          const consultant = project.consultant_name && project.consultant_name !== "Unassigned" ? project.consultant_name : null;
          if (project.start_date || project.created_at) {
            calEvents.push({
              id: `proj_start_${t.id}`,
              type: "start",
              date: new Date(project.start_date || project.created_at),
              company: t.name || t.id,
              contact: t.contact_name || "",
              consultant,
              status: project.status,
              tenant_id: t.id,
            });
          }
          if (project.target_end_date || project.end_date) {
            calEvents.push({
              id: `proj_end_${t.id}`,
              type: "end",
              date: new Date(project.target_end_date || project.end_date),
              company: t.name || t.id,
              contact: t.contact_name || "",
              consultant,
              status: project.status,
              tenant_id: t.id,
            });
          }
        } catch { /* skip */ }
      }

      // Calendly-sourced sessions
      for (const c of calendlySessions) {
        if (!c.start_time) continue;
        const linkedProspect = c.prospect_id ? prospects.find((p) => p.id === c.prospect_id) : null;
        const consultant = linkedProspect ? getConsultant(linkedProspect) : null;
        calEvents.push({
          id: c.id || `calendly_${c.calendly_event_id}`,
          type: c.session_type || "call",
          date: new Date(c.start_time),
          company: c.company_name || c.invitee_name || "Calendly Invitee",
          contact: c.invitee_name || "",
          consultant,
          status: c.status || "scheduled",
          notes: c.event_name,
          prospect_id: c.prospect_id,
          source: "calendly",
        });
      }

      // Normalize legacy types (defensive)
      for (const e of calEvents) {
        if (TYPE_REMAP[e.type]) e.type = TYPE_REMAP[e.type];
      }

      setEvents(calEvents.sort((a, b) => a.date - b.date));
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Reset expanded state on month change → fixes stale data display bug
  useEffect(() => { setExpandedCells(new Set()); }, [currentDate]);

  const filteredEvents = useMemo(() => {
    if (consultantFilter === "all") return events;
    return events.filter((e) => (e.consultant || "—") === consultantFilter);
  }, [events, consultantFilter]);

  // Calendar grid for current month
  const grid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startWeekday = firstDay.getDay();
    const cells = [];
    // Prev month padding
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      cells.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push({ date: new Date(year, month, d), inMonth: true });
    }
    // Next month padding to fill last row
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const next = new Date(last); next.setDate(last.getDate() + 1);
      cells.push({ date: next, inMonth: false });
    }
    return cells;
  }, [currentDate]);

  const eventsForDay = useCallback((date) => filteredEvents.filter((e) => sameDay(e.date, date)), [filteredEvents]);

  // Stats — month-scoped
  const stats = useMemo(() => {
    const now = new Date();
    const monthEvents = filteredEvents.filter((e) => e.date.getFullYear() === currentDate.getFullYear() && e.date.getMonth() === currentDate.getMonth());
    return {
      total: monthEvents.length,
      upcoming: filteredEvents.filter((e) => e.date >= new Date(now.getFullYear(), now.getMonth(), now.getDate())).length,
      unassigned: filteredEvents.filter((e) => !e.consultant).length,
      starting: monthEvents.filter((e) => e.type === "start").length,
    };
  }, [filteredEvents, currentDate]);

  // Consultant load — month-scoped, filtered by current consultantFilter
  const consultantLoad = useMemo(() => {
    const month = filteredEvents.filter((e) => e.date.getFullYear() === currentDate.getFullYear() && e.date.getMonth() === currentDate.getMonth());
    const map = new Map();
    for (const e of month) {
      const k = e.consultant || "—";
      map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [filteredEvents, currentDate]);

  const next5 = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return filteredEvents.filter((e) => e.date >= now).slice(0, 5);
  }, [filteredEvents]);

  const today = new Date();

  const changeMonth = (dir) => {
    const next = new Date(currentDate);
    next.setMonth(next.getMonth() + dir);
    setCurrentDate(next);
  };

  const consultantOptions = useMemo(() => {
    const set = new Set();
    for (const e of events) set.add(e.consultant || "—");
    return ["all", ...Array.from(set).sort()];
  }, [events]);

  return (
    <div className="max-w-[1600px] mx-auto" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="calendar-v2">
      {/* Top Bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-4">
          <h1 className="text-[24px] font-bold tracking-tight" style={{ color: "#0F1117" }}>Calendar</h1>
          <div className="flex items-center gap-1 ml-2">
            <button type="button" onClick={() => changeMonth(-1)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-[#FAFAF8]" data-testid="cal-prev-month">
              <ChevronLeft className="h-4 w-4" style={{ color: "#5C6070" }} />
            </button>
            <div className="px-2 text-[15px] font-semibold tabular-nums" style={{ color: "#0F1117" }} data-testid="cal-current-month">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            <button type="button" onClick={() => changeMonth(1)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-[#FAFAF8]" data-testid="cal-next-month">
              <ChevronRight className="h-4 w-4" style={{ color: "#5C6070" }} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}
            className="px-3 py-2 rounded-[8px] border bg-white text-[13px] outline-none focus:border-[#3B4FE4]"
            style={{ borderColor: "rgba(0,0,0,0.08)", color: "#0F1117" }}
            data-testid="cal-consultant-filter"
          >
            {consultantOptions.map((c) => (
              <option key={c} value={c}>{c === "all" ? "All Consultants" : c}</option>
            ))}
          </select>
          <button type="button" className="inline-flex items-center gap-2 h-9 px-3 rounded-[8px] text-[13px] font-semibold text-white" style={{ background: "#3B4FE4" }} data-testid="cal-new-session-btn">
            <Plus className="h-4 w-4" /> New Session
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="cal-legend">
        {Object.entries(SESSION_TYPES).map(([k, t]) => (
          <span key={k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: t.bg, color: t.color }}>
            <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
            {t.label}
          </span>
        ))}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cal-stats">
        {[
          { label: "Sessions This Month", value: stats.total, key: "total" },
          { label: "Upcoming", value: stats.upcoming, key: "upcoming" },
          { label: "Unassigned", value: stats.unassigned, key: "unassigned" },
          { label: "Projects Starting", value: stats.starting, key: "starting" },
        ].map((s) => (
          <div key={s.key} className="rounded-xl border bg-white px-4 py-3" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid={`cal-stat-${s.key}`}>
            <div className="text-[10px] font-bold tracking-[0.10em] uppercase" style={{ color: "#9499A8" }}>{s.label}</div>
            <div className="text-[22px] font-bold tabular-nums mt-1" style={{ color: "#0F1117" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4">
        {/* Calendar Grid */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(0,0,0,0.08)", background: "#FAFAF8" }}>
          <div className="grid grid-cols-7" style={{ background: "#fff", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            {DAYS.map((d) => (
              <div key={d} className="px-3 py-2 text-[11px] font-bold tracking-[0.06em] uppercase" style={{ color: "#5C6070" }}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 p-1" style={{ gridAutoRows: "minmax(120px, 1fr)" }}>
            {grid.map((cell, idx) => {
              const dayEvents = eventsForDay(cell.date);
              const isToday = sameDay(cell.date, today);
              const expanded = expandedCells.has(cell.date.toISOString());
              const visible = expanded ? dayEvents : dayEvents.slice(0, 3);
              const hidden = dayEvents.length - visible.length;
              const isWeekend = cell.date.getDay() === 0 || cell.date.getDay() === 6;
              const cellBg = !cell.inMonth
                ? "rgba(255,255,255,0.5)"
                : isToday
                  ? "#EEF0FD"
                  : isWeekend ? "rgba(249,250,251,0.7)" : "#fff";
              return (
                <div
                  key={idx}
                  className="rounded-lg overflow-hidden"
                  style={{
                    background: cellBg,
                    outline: isToday ? "1.5px solid #3B4FE4" : "none",
                    minHeight: 120,
                  }}
                  data-testid={`cal-cell${isToday ? "-today" : ""}`}
                >
                  <div className="flex items-center justify-between px-2 pt-2">
                    <span
                      className="inline-flex items-center justify-center text-[11px] font-bold tabular-nums"
                      style={isToday
                        ? { width: 22, height: 22, borderRadius: "50%", background: "#3B4FE4", color: "#fff" }
                        : { color: cell.inMonth ? "#2D3142" : "#C6CADE" }}
                    >
                      {cell.date.getDate()}
                    </span>
                  </div>
                  <div className="px-1.5 pb-1.5 mt-1 space-y-1" style={{ overflow: "hidden" }}>
                    {visible.map((e) => {
                      const t = SESSION_TYPES[e.type] || SESSION_TYPES.intake;
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => setDetailEvent(e)}
                          className="w-full text-left flex items-center gap-1.5 px-1.5 py-1 rounded-[5px] text-[11px]"
                          style={{ background: t.bg, color: t.color, border: `1px solid ${t.color}22` }}
                          data-testid={`cal-event-${e.id}`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: t.color }} />
                          <span className="truncate font-semibold flex-1" style={{ color: "#0F1117" }}>{e.company}</span>
                          <span className="shrink-0" style={{ fontSize: 10, opacity: 0.75 }}>{e.consultant ? initialsOf(e.consultant) : "—"}</span>
                        </button>
                      );
                    })}
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = new Set(expandedCells);
                          next.add(cell.date.toISOString());
                          setExpandedCells(next);
                        }}
                        className="w-full text-left text-[10px] font-semibold px-1.5 py-0.5 rounded hover:bg-[rgba(59,79,228,0.06)]"
                        style={{ color: "#3B4FE4" }}
                        data-testid="cal-more-btn"
                      >
                        +{hidden} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="cal-consultant-load">
            <div className="text-[10px] font-bold tracking-[0.10em] uppercase mb-3" style={{ color: "#9499A8" }}>Consultant Load — {MONTHS[currentDate.getMonth()].slice(0, 3)}</div>
            {consultantLoad.length === 0 && <div className="text-[12px]" style={{ color: "#9499A8" }}>No sessions this month.</div>}
            {consultantLoad.map((c) => {
              const max = Math.max(...consultantLoad.map((x) => x.count), 1);
              const pct = (c.count / max) * 100;
              const color = c.count > 15 ? "#DC2626" : c.count >= 8 ? "#D97706" : "#3B4FE4";
              return (
                <div key={c.name} className="mb-2 last:mb-0">
                  <div className="flex items-center justify-between text-[12px] mb-1" style={{ color: "#2D3142" }}>
                    <span className="truncate font-semibold">{c.name}</span>
                    <span className="tabular-nums" style={{ color: "#5C6070" }}>{c.count}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
                    <div style={{ width: `${pct}%`, background: color, height: "100%" }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="cal-next-5">
            <div className="text-[10px] font-bold tracking-[0.10em] uppercase mb-3" style={{ color: "#9499A8" }}>Next 5 Sessions</div>
            {next5.length === 0 && <div className="text-[12px]" style={{ color: "#9499A8" }}>No upcoming sessions.</div>}
            {next5.map((e) => {
              const t = SESSION_TYPES[e.type] || SESSION_TYPES.intake;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setDetailEvent(e)}
                  className="w-full text-left flex items-center gap-2 py-2 border-b last:border-b-0"
                  style={{ borderColor: "rgba(0,0,0,0.06)" }}
                >
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold truncate" style={{ color: "#0F1117" }}>{e.company}</div>
                    <div className="text-[10px]" style={{ color: "#5C6070" }}>
                      {e.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {e.consultant ? initialsOf(e.consultant) : "—"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail slide-in panel */}
      {detailEvent && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setDetailEvent(null)} style={{ background: "rgba(15,17,23,0.20)" }} />
          <aside
            className="fixed top-0 right-0 z-50 h-screen bg-white shadow-2xl border-l flex flex-col"
            style={{ borderColor: "rgba(0,0,0,0.08)", width: 340 }}
            data-testid="cal-detail-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
              <div className="text-[11px] font-bold tracking-[0.10em] uppercase" style={{ color: "#9499A8" }}>Session Detail</div>
              <button type="button" onClick={() => setDetailEvent(null)} className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-[#FAFAF8]"><X className="h-4 w-4" style={{ color: "#5C6070" }} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {(() => {
                const t = SESSION_TYPES[detailEvent.type] || SESSION_TYPES.intake;
                return (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: t.bg, color: t.color }}>
                    <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                    {t.label}
                  </span>
                );
              })()}
              <div>
                <div className="text-[16px] font-bold" style={{ color: "#0F1117" }}>{detailEvent.company}</div>
                {detailEvent.contact && <div className="text-[13px]" style={{ color: "#5C6070" }}>{detailEvent.contact}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <Field label="Date" value={fmtDate(detailEvent.date)} />
                <Field label="Consultant" value={detailEvent.consultant || "—"} />
                <Field label="Status" value={detailEvent.status || "Upcoming"} />
                <Field label="Stage" value={detailEvent.stage || "—"} />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.10em] uppercase mb-1" style={{ color: "#9499A8" }}>Notes</div>
                <div className="text-[13px]" style={{ color: "#2D3142" }}>{detailEvent.notes || "No notes added yet."}</div>
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
              <button type="button" onClick={() => setDetailEvent(null)} className="text-[13px] font-semibold" style={{ color: "#5C6070" }}>Close</button>
              <button
                type="button"
                onClick={() => {
                  if (detailEvent.prospect_id) navigate(`/app/customers/${detailEvent.prospect_id}`);
                  else if (detailEvent.tenant_id) navigate(`/app/tenants/${detailEvent.tenant_id}`);
                }}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
                style={{ color: "#3B4FE4" }}
                data-testid="cal-detail-view-btn"
              >
                View Full Record <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>
          </aside>
        </>
      )}

      {loading && events.length === 0 && (
        <div className="text-center py-10 text-[13px]" style={{ color: "#9499A8" }}>Loading sessions…</div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-bold tracking-[0.10em] uppercase mb-0.5" style={{ color: "#9499A8" }}>{label}</div>
    <div className="text-[13px]" style={{ color: "#0F1117" }}>{value}</div>
  </div>
);
